import { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import PropTypes from "prop-types";
import classNames from "classnames";

import { setSelectedNearbyPoiInfo } from "@/src/store/listingDetail";

import { getNearbyPOIs } from "services/listingService";

import { Ga4WithEventParameters } from "@/src/helpers/google-tag-manager/events/ga4-with-eventparameters";
import { poiTypes, getGa4EventForPoiId } from "src/utils/constants/poi-types";

import PoiFilterBox from "src/components/listing-detail/poi-map/poi-filterbox";
import Loading from "components/common/loading-spinner";
import { LazyImage } from "src/components/common/lazy-image/LazyImage";

import styles from "./styles.module.css";

const ROAD_VIEW = "ROAD_VIEW";
const PANORAMA_VIEW = "PANORAMA_VIEW";
const SATELLITE_VIEW = "SATELLITE_VIEW";

const MapTab = ({ location, listingId, url, usePoiFilterBox, useSatellite, pageType }) => {
  const { lat, lon } = location?.coordinate || {};
  const dispatch = useDispatch();
  const selectedNearbyPoiInfo = useSelector((state) => state.listingDetail.selectedNearbyPoiInfo);
  const selectedPlacemarkRef = useRef(null);
  const isFirstLoad = useRef(true);
  const [poiList, setPoiList] = useState([]);
  const [mapViewLoading, setMapViewLoading] = useState(true);
  const [panoramaViewLoading, setPanoramaViewLoading] = useState(true);
  const [groupedPois, setGroupedPois] = useState({});
  const [placeMarkerEvents, setPlaceMarkerEvents] = useState([]);
  const [activeMapType, setActiveMapType] = useState(ROAD_VIEW);
  const [isHavePanoramaView, setIsHavePanoramaView] = useState(false);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const listingLocationProperty = [lat, lon];
  const isProjectPage = pageType === "project";
  const selectedNearbyPoiId = selectedNearbyPoiInfo?.item?.id;

  const mapView = classNames({
    [styles.mapAndSatelliteButton]: true,
    [styles.activeButton]: activeMapType === ROAD_VIEW,
  });

  const satelliteViewTabs = classNames({
    [styles.mapAndSatelliteButton]: true,
    [styles.activeButton]: activeMapType === SATELLITE_VIEW,
  });

  const initializeMapScript = () => {
    const MAP_API_KEY = window.env.EJ_YANDEX_API_KEY;
    if (!window.ymaps) {
      const script = document.createElement("script");
      script.type = "text/javascript";
      script.src = `https://api-maps.yandex.ru/2.1/?apikey=${MAP_API_KEY}&lang=tr_TR`;
      const partOfScript = document.getElementsByTagName("script")[0];
      partOfScript.parentNode.insertBefore(script, partOfScript);

      script.addEventListener("load", () => {
        setIsScriptLoaded(true);
      });
    } else {
      setIsScriptLoaded(true);
    }
  };

  const onScriptLoad = () => {
    const mapCenter = listingLocationProperty;

    window.ymaps.ready(() => {
      window.map = new window.ymaps.Map(ROAD_VIEW, {
        center: mapCenter,
        zoom: 15,
        controls: [],
        type: `yandex#${activeMapType === "ROAD_VIEW" ? "map" : "satellite"}`,
      });

      const hasLocations = location?.coordinate;

      if (hasLocations) {
        setPreloadLocation(location.coordinate);

        const myPlacemark = new window.ymaps.Placemark(
          [lat, lon],
          {},
          {
            iconLayout: "default#image",
            iconImageHref: "/assets/images/map/emlakjet-map-center.png",
            iconImageSize: [40, 52],
            hideIconOnBalloonOpen: false,
          }
        );

        const collection = new window.ymaps.GeoObjectCollection();
        collection.add(myPlacemark);

        window.map.geoObjects.add(collection);
      }

      const zoomControl = new window.ymaps.control.ZoomControl({
        options: {
          size: "small",
          position: {
            position: "absolute",
            right: 20,
          },
        },
      });

      window.map.controls.add(zoomControl);
      const controlElement = document.querySelector(".ymaps-2-1-79-controls__control");
      const marginTop = !isProjectPage ? "110px" : activeMapType === ROAD_VIEW ? "60px" : "100px";
      Object.assign(controlElement.style, { marginTop });
      addGeoShapes();
      // Add selected POIs to the map after it's initialized
      if (Object.keys(groupedPois).length > 0) {
        handleAddPlaceMarkToMap();
      }
      setMapViewLoading(false);
    });
  };

  const addGeoShapes = () => {
    const geoShapes = location?.geoShape;
    const isListingHaveGeoShape = geoShapes?.length > 0 || false;
    if (!isListingHaveGeoShape) return;
    const geoShapeList = geoShapes?.map((point) => [point.lat, point.lon]);
    const myGeoObject = new window.ymaps.GeoObject(
      {
        geometry: {
          type: "Polygon",
          coordinates: [[...geoShapeList]],
          fillRule: "nonZero",
        },
      },
      {
        fillColor: "#52ca46",
        strokeColor: "#52ca46",
        strokeWidth: 3,
        fillOpacity: 0.2,
      }
    );

    window.map.geoObjects.add(myGeoObject);
  };

  const showPanorama = (coords) => {
    setPanoramaViewLoading(true);
    setActiveMapType(PANORAMA_VIEW);
    window.ymaps.panorama.locate(coords).done(
      (panoramas) => {
        if (panoramas.length > 0) {
          const player = new window.ymaps.panorama.Player(PANORAMA_VIEW, panoramas[0], {
            direction: [256, 16],
          });
          player.events.add("destroy", () => {
            setActiveMapType(ROAD_VIEW);
          });
          setIsHavePanoramaView(true);
          manipulateToDOMElements();
        } else {
          setIsHavePanoramaView(false);
        }
        setPanoramaViewLoading(false);
      },
      (error) => {
        console.error(error);
        setActiveMapType("ROAD_VIEW");
      }
    );
  };

  const manipulateToDOMElements = () => {
    const closeButton = document.querySelector(".ymaps-2-1-79-islets_button-icon_cross");
    if (closeButton) {
      const containerElement = closeButton.parentElement.parentElement;
      // full screen & open for ymaps option element override css
      closeButton.parentElement.remove();
      document.querySelector(".ymaps-2-1-79-panorama-control__fullscreen")?.remove();
      document.querySelector(".ymaps-2-1-79-panorama-control__inception")?.remove();
      // Replace Close Page Icon to Custom Icon in Panorama View
      const newDivElement = document.createElement("div");
      Object.assign(newDivElement.style, {
        backgroundImage: 'url("/assets/images/map/road-view-icon.png")',
        height: "100%",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
      });
      containerElement.appendChild(newDivElement);
      const containerMarginTop = isProjectPage ? "60px" : "auto";
      Object.assign(containerElement.style, { marginTop: containerMarginTop });

      // Fix all icons centering bug
      const allMapElement = document.querySelectorAll(".ymaps-2-1-79-islets_round-button__icon");
      allMapElement.forEach((mapElement) => {
        mapElement.style.width = "100%";
        mapElement.style.height = "100%";
      });

      // zoom element override css
      const controlZoomElementsContainer = document.querySelector(".ymaps-2-1-79-panorama-control__zoom");
      const zoomContainerTop = isProjectPage ? "110px" : "60px";
      Object.assign(controlZoomElementsContainer.style, {
        left: "auto",
        right: "20px",
        top: zoomContainerTop,
      });
      const plusIcon = document.querySelector(".ymaps-2-1-79-islets_button-icon_plus");
      const minusIcon = document.querySelector(".ymaps-2-1-79-islets_button-icon_minus");
      Object.assign(plusIcon.style, {
        backgroundImage: 'url("/assets/icons/plus_icon.svg")',
      });
      Object.assign(minusIcon.style, {
        backgroundImage: 'url("/assets/icons/minus_icon.svg")',
      });

      // Panaromada adress title elementine margin-top eklemesi
      const addressElement = document.querySelector("[class*='ymaps-'][class*='panorama-control__name']");
      if (addressElement) {
        const addressMarginTop = isProjectPage ? "40px" : "auto";
        Object.assign(addressElement.style, { marginTop: addressMarginTop });
      }
    }
  };

  const setPreloadLocation = (currentLocations) => {
    const { lat, lon } = currentLocations;
    window.map.setCenter([lat, lon], 16);
  };

  const getPoiList = async () => {
    const defaultPOIs = [11, 7, 8, 2, 5, 1, 3, 4, 9, 6, 10];
    try {
      const response = await getNearbyPOIs({ id: listingId, limit: 30, categories: defaultPOIs });
      setPoiList(response?.result);
      setGroupedPoiList(response?.result);
    } catch (error) {
      console.error("Error from getPoiList: ", error);
    }
  };

  const setGroupedPoiList = (poiList = []) => {
    let imageHref = "";
    const groupedPOIs = {};

    poiList.forEach((categories) => {
      imageHref = poiTypes.find((poi) => poi.id === categories.categoryId)?.placemarkImageHref;

      const isInitiallySelected = selectedNearbyPoiInfo?.categoryId === categories.categoryId;

      groupedPOIs[categories.categoryId] = {
        isSelected: isInitiallySelected || false,
        poiList: categories.poies.map((poi) => ({
          id: poi.id,
          name: poi.name,
          center: [poi.coordinates.lat, poi.coordinates.lon],
          coordinates: poi.coordinates,
          distance: poi.distance,
          type: "poi",
          categoryId: categories.categoryId,
          subcategoryId: null,
          imageHref,
        })),
      };
    });

    setGroupedPois(groupedPOIs);
    handleAddPlaceMarkToMap();
  };

  const handleChangePoi = async (event, selectedPoi) => {
    const isPoiFieldSelected = event.target.checked;
    const { categoryId } = selectedPoi || {};
    const selectedGroupedPoiList = groupedPois[categoryId];
    selectedGroupedPoiList.isSelected = isPoiFieldSelected;
    setGroupedPois({ ...groupedPois });
    handleAddPlaceMarkToMap();
    if (isPoiFieldSelected) {
      Ga4WithEventParameters({
        eventName: "maps",
        eventParameters: { label: `click||${getGa4EventForPoiId[categoryId]}` },
      });
    }
  };

  const handleAddPlaceMarkToMap = () => {
    removePOIs(window.map);
    const selectedPoiList = Object.values(groupedPois).filter((poiList) => poiList.isSelected);
    let balloonOpened = false;

    selectedPoiList.forEach((poiList) => {
      poiList.poiList.forEach((poi) => {
        const {
          imageHref,
          name,
          distance,
          coordinates: { lat, lon },
        } = poi || {};

        const isFocused = poi.id === selectedNearbyPoiId;
        let CustomBalloonLayout = null;
        if (isFocused) {
          CustomBalloonLayout = window.ymaps.templateLayoutFactory.createClass(`
          <div style="
            font-family: 'Open Sans', sans-serif;
            background: white;
            border-radius: 20px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.15);
            padding: 5px 10px;
            text-align: center;
            width: max-content;
          ">
            <p style="margin: 0; font-weight: 600; font-size: 12px; color: #000;">
              $[properties.name]
            </p>
            <p style="font-size: 10px; color: #5F787E; font-weight: 600;">
              $[properties.distance] uzaklığında
            </p>
          </div>
        `);
        }

        const placemark = new window.ymaps.Placemark(
          [lat, lon],
          isFocused ? { name, distance } : { hintContent: name },
          {
            iconLayout: "default#image",
            iconImageHref: imageHref,
            iconImageSize: [35, 35],
            hideIconOnBalloonOpen: false,
            ...(isFocused && {
              balloonLayout: CustomBalloonLayout,
              balloonOffset: [-60, -80],
              balloonPanelMaxMapArea: 0,
            }),
          }
        );

        placeMarkerEvents.push(placemark);
        window.map.geoObjects.add(placemark);

        if (isFocused && !balloonOpened) {
          if (isFirstLoad.current) {
            placemark.balloon.open();
            isFirstLoad.current = false;
          }
          selectedPlacemarkRef.current = placemark;
          balloonOpened = true;
        }
      });
    });

    setPlaceMarkerEvents(placeMarkerEvents);
  };

  const removePOIs = (map) => {
    placeMarkerEvents.map((filteredPOI) => map.geoObjects.remove(filteredPOI));
  };

  const mapButtonClass = (type) =>
    classNames({
      [styles.mapTypeButton]: true,
      [styles.active]: activeMapType === type,
    });

  useEffect(() => {
    if (!isScriptLoaded) {
      initializeMapScript();
      return;
    }

    window.ymaps?.ready(() => {
      if (window.map) {
        window.map.destroy();
      }
      onScriptLoad();
    });
  }, [isScriptLoaded, activeMapType]);

  useEffect(() => {
    const isHaveSelectedPoi = Object.values(groupedPois).some((groupedPoi) => groupedPoi.isSelected);
    if (isHaveSelectedPoi && window.map) {
      handleAddPlaceMarkToMap();
    }
  }, [groupedPois]);

  useEffect(() => {
    getPoiList();

    // to close the balloon content opened for a specific poi
    if (window?.map?.events) {
      window.ymaps.ready(() => {
        window.map.events.add("click", function () {
          if (selectedPlacemarkRef.current && selectedPlacemarkRef.current.balloon.isOpen()) {
            selectedPlacemarkRef.current.balloon.close();
          }
        });
      });
    }

    // Clear all selects
    return () => {
      if (window.map) {
        removePOIs(window.map);
      }
      setPlaceMarkerEvents([]);
      dispatch(setSelectedNearbyPoiInfo(null));
      setGroupedPois((prev) => {
        const resetGrouped = {};
        Object.entries(prev).forEach(([categoryId, group]) => {
          resetGrouped[categoryId] = {
            ...group,
            isSelected: false,
          };
        });
        return resetGrouped;
      });
    };
  }, []);

  return (
    <>
      {isProjectPage && (
        <div className={styles.mapTypeButtons}>
          <button className={mapButtonClass(ROAD_VIEW)} onClick={() => setActiveMapType(ROAD_VIEW)}>
            Harita
          </button>
          <button className={mapButtonClass(PANORAMA_VIEW)} onClick={() => showPanorama([lat, lon])}>
            Sokak Görünümü
          </button>
        </div>
      )}
      {usePoiFilterBox && !isProjectPage && (
        <PoiFilterBox
          poiList={poiList}
          handleChangePoi={handleChangePoi}
          activeMapType={activeMapType}
          selectedNearbyPoiId={selectedNearbyPoiInfo?.categoryId}
          groupedPois={groupedPois}
        />
      )}
      {activeMapType === SATELLITE_VIEW && (
        <div id={ROAD_VIEW} style={{ width: "100%", height: "100%", position: "relative" }}>
          <>
            {!useSatellite && !isProjectPage && (
              <div className={styles.tabWrapper}>
                <div className={mapView} onClick={() => setActiveMapType(ROAD_VIEW)}>
                  Harita
                </div>
                <div className={satelliteViewTabs} onClick={() => setActiveMapType(SATELLITE_VIEW)}>
                  Uydu Görünümü
                </div>
              </div>
            )}
            {mapViewLoading && (
              <div className={styles.loadingContainer}>
                <Loading loadingClass={styles.loading} />
              </div>
            )}
            {!isProjectPage && (
              <div className={styles.panoramaViewIcon} onClick={() => showPanorama([lat, lon])}>
                <LazyImage src="/assets/images/map/panorama-view-icon.png" alt="Panorama görünümüne geç" />
              </div>
            )}
          </>
        </div>
      )}

      {activeMapType === ROAD_VIEW && (
        <>
          <div id={ROAD_VIEW} style={{ width: "100%", height: "100%", position: "relative" }}>
            <>
              {!useSatellite && !isProjectPage && (
                <div className={styles.tabWrapper}>
                  <div className={mapView} onClick={() => setActiveMapType(ROAD_VIEW)}>
                    Harita
                  </div>
                  <div className={satelliteViewTabs} onClick={() => setActiveMapType(SATELLITE_VIEW)}>
                    Uydu Görünümü
                  </div>
                </div>
              )}
              {mapViewLoading && (
                <div className={styles.loadingContainer}>
                  <Loading loadingClass={styles.loading} />
                </div>
              )}
              {!isProjectPage && (
                <div className={styles.panoramaViewIcon} onClick={() => showPanorama([lat, lon])}>
                  <LazyImage src="/assets/images/map/panorama-view-icon.png" alt="Panorama görünümüne geç" />
                </div>
              )}
            </>
          </div>
        </>
      )}
      {activeMapType === PANORAMA_VIEW && (
        <div id={PANORAMA_VIEW} style={{ width: "100%", height: "100%", position: "relative" }}>
          {panoramaViewLoading ? (
            <div className={styles.loadingContainer}>
              <Loading loadingClass={styles.loading} />
            </div>
          ) : (
            !isHavePanoramaView && (
              <div className={styles.noPanoramaViewContainer}>
                <p className={styles.noPanoramaViewText}>Sokak Görünümü Bulunamadı</p>
                {!isProjectPage && (
                  <div className={styles.panoramaViewIcon} onClick={() => setActiveMapType("ROAD_VIEW")}>
                    <LazyImage src="/assets/images/map/road-view-icon.png" alt="Harita görünümüne geç" />
                  </div>
                )}
              </div>
            )
          )}
        </div>
      )}
    </>
  );
};

MapTab.propTypes = {
  location: PropTypes.shape({
    coordinate: PropTypes.shape({
      lat: PropTypes.number.isRequired,
      lon: PropTypes.number.isRequired,
    }).isRequired,
    geoShape: PropTypes.arrayOf(
      PropTypes.shape({
        lat: PropTypes.number.isRequired,
        lon: PropTypes.number.isRequired,
      })
    ),
  }).isRequired,
  listingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  url: PropTypes.string,
  pageType: PropTypes.string,
  usePoiFilterBox: PropTypes.bool,
  useSatellite: PropTypes.bool,
};

export default MapTab;
